import {
  type Ref,
  ref,
  computed,
  watch,
  nextTick,
  readonly,
  unref,
  toRefs,
} from "vue";
import { useBackendService } from "./services/useBackend.service";
import { useCacheableBackendService } from "./services/useCacheableBackend.service";
import { useCookie, useRuntimeConfig } from "#app";
import { onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAccountStore } from "~/stores/account";
import { useAuthStore } from "~/stores/auth";
import { storeToRefs } from 'pinia'
import { invalidateAllGraphQLCache } from '@/utils/graphqlCache';
import { usePostHog } from "~/composables/usePostHog"; // Import PostHog
import { navigateTo } from '#app';
import { sanitizeResponse, sanitizeCookie } from '~/utils/sanitizeResponse';

export interface User {
  id: number;
  email: string;
  username: string;
  customerId: number;
  title?: string;
  firstName?: string;
  lastName?: string;
  emailVerified?: boolean;
  identityVerified?: boolean;
  dateOfBirth?: string;
  siteActiveId?: string;
}

interface StrapiUserResponse extends User {
  provider: string;
  confirmed: boolean;
  blocked: boolean;
  createdAt: string;
  updatedAt: string;
}

// Define the possible response types from the session API
type SessionErrorResponse = {
  success: boolean;
  error: { message: string };
  user?: User; // Add optional user property to fix lint errors
};

type SessionSuccessResponse = {
  siteActiveId: string;
  success: boolean;
  user?: User;
};

type SessionAuthResponse = {
  success: boolean;
  isPublicRoute: boolean;
  authRequired: boolean;
  authenticated: boolean;
  user?: User; // Add user property to fix lint errors
};

// Combined type for all possible session responses
type SessionResponse = SessionErrorResponse | SessionSuccessResponse | SessionAuthResponse;

export const useAuth = () => {
  const router = useRouter();
  const config = useRuntimeConfig();
  const authStore = useAuthStore();
  
  // Get reactive refs from store
  const { user, loading, error, status, unauthenticatedReason, siteActiveIdDebug, sessionTimeoutHandled } = storeToRefs(authStore);
  
  const sessionCookie = useCookie("SITEACTIVEID", {
    path: "/",
    watch: true,
  });
  
  // Cookie options interface to properly type the options
  interface CookieOptions {
    path: string;
    watch: boolean;
    maxAge?: number;
  }

  // Cookie options for persistent cookies (remember me) - Now set to 60 minutes
  const getPersistentCookieOptions = (): CookieOptions => {
    return {
      path: "/",
      watch: true,
      maxAge: 60 * 60, // 60 minutes in seconds (3600)
    };
  };

  // Cookie options for session cookies (default) - Now also set to 60 minutes for consistency
  const getSessionCookieOptions = (): CookieOptions => {
    return {
      path: "/",
      watch: true,
      maxAge: 60 * 60, // 60 minutes in seconds (3600)
    };
  };
  const { signInBackend } = useBackendService();

  // Update status based on current state
  const updateStatus = () => {
    if (authStore.loading) {
      authStore.status = "loading";
      return;
    }

    if (authStore.user && authStore.user.customerId) {
      authStore.status = "authenticated";
      
      // PostHog: Identify user when authenticated
      if (typeof window !== 'undefined') {
        try {
          const { identifyUser } = usePostHog();
          identifyUser({
            customerId: authStore.user.customerId.toString(),
            email: authStore.user.email,
            name: `${authStore.user.firstName || ''} ${authStore.user.lastName || ''}`.trim() || authStore.user.email,
          });
        } catch (error) {
          console.warn('[PostHog] Failed to identify user:', error);
        }
      }
    } else {
      authStore.status = "unauthenticated";
    }
  };

  // Get current session
  // Uses global caching to reduce API calls
  const { getSession: getSessionCacheable } = useCacheableBackendService();
  const getSession = async (forceFresh = false): Promise<any> => {
    const startTime = Date.now();
    console.log("[Auth Diagnostics] getSession called with forceFresh =", forceFresh);
    
    try {
      // The cacheable backend service now handles in-memory caching and request deduplication
      // This prevents redundant calls to /api/custom-auth/me during component mounting
      console.log('[useAuth] Requesting session data', forceFresh ? '(force fresh)' : '(cached if available)');
      
      // Check if we can use cached session data from sessionStorage (longer-term caching for page refreshes)
      if (!forceFresh && typeof sessionStorage !== 'undefined') {
        const sessionLastRefreshed = sessionStorage.getItem('auth_session_last_refreshed');
        const cacheValidFor = 120000; // 2 minutes for longer-term storage
        const cacheValid = sessionLastRefreshed ? 
          (Date.now() - parseInt(sessionLastRefreshed || '0', 10)) < cacheValidFor : false;
          
        if (cacheValid) {
          const cachedData = sessionStorage.getItem('auth_session_data');
          if (cachedData) {
            try {
              const cachedSessionData = JSON.parse(cachedData);
              console.log('[useAuth] Using session data from sessionStorage, last refreshed',
                new Date(parseInt(sessionLastRefreshed || '0', 10)).toLocaleTimeString());
              
              // Return the cached data without making an API call
              authStore.loading = false;
              return cachedSessionData;
            } catch (e: unknown) {
              console.warn('[useAuth] Failed to parse cached session data from sessionStorage', 
                e instanceof Error ? e.message : 'Unknown error');
            }
          }
        }
      }
      
      // If we get here, we'll use the cacheable backend service which has short-term caching
      // This prevents redundant API calls during component mounting phase
      const response = await getSessionCacheable(forceFresh) as SessionResponse;
      
      // Validate and clear stale cache if necessary
      validateAndClearStaleCache(response.siteActiveId);
      
      // Cache the successful response for future use
      if (response && !forceFresh) {
        // Return the original unsanitized data for use in authentication
        return response;
      }

      // Type guard function for SessionSuccessResponse
      const isSuccessResponse = (res: SessionResponse): res is SessionSuccessResponse => 
        'siteActiveId' in res && typeof res.siteActiveId === 'string';
      
      // Type guard function for SessionErrorResponse
      const isErrorResponse = (res: SessionResponse): res is SessionErrorResponse => 
        'error' in res && typeof res.error === 'object';

      // Handle success response
      if (isSuccessResponse(response)) {
        siteActiveIdDebug.value = response.siteActiveId;
      } else {
        siteActiveIdDebug.value = "XXX";
      }
      
      // Check for error patterns in response first
      if (isErrorResponse(response)) {
        console.error(
          "getSession - API returned error:",
          response.error.message
        );
        authStore.unauthenticatedReason = "Session API error: " + response.error.message;
        return null;
      }
      
      // For responses with a status field (legacy format)
      if ('status' in response && 
          (response.status === "FAIL" || response.status === "ERROR")) {
        console.error("getSession - API returned status error:", response.status);
        authStore.unauthenticatedReason = "Session API error: " + response.status;
        return null;
      }

      // Extended user data type for account store initialization
      interface ExtendedUserData extends User {
        documentId?: string;
        confirmed?: boolean;
        blocked?: boolean;
        locale?: string;
        createdAt?: string;
        updatedAt?: string;
        publishedAt?: string;
      }

      // Type guard for user data
      const isUserData = (data: any): data is ExtendedUserData => {
        return data && typeof data === 'object' && 'id' in data && 'email' in data;
      };

      // Extract user data - it might be nested in response.user or directly on response
      let userData: any = null;
      
      if (isSuccessResponse(response) && response.user) {
        userData = response.user;
      } else if (isSuccessResponse(response)) {
        userData = response;
      } else if ('user' in response && isUserData(response.user)) {
        userData = response.user;
      } else {
        userData = response;
      }

      // If we got a valid response with user data
      if (isUserData(userData)) {
        console.log(
          "Received valid user profile from cacheable backend service"
        );
        authStore.setUser({
          id: userData.id,
          email: userData.email,
          username: userData.username,
          customerId: userData.customerId,
          title: userData.title,
          firstName: userData.firstName,
          lastName: userData.lastName,
          emailVerified: userData.emailVerified,
          identityVerified: userData.identityVerified,
          dateOfBirth: userData.dateOfBirth,
          siteActiveId: userData.siteActiveId,
        });

        // Initialize account store with basic user data only (addresses loaded when needed)
        const accountStore = useAccountStore();
        
        // Preserve any existing addresses and payment methods from localStorage persistence
        const existingAddresses = accountStore.account?.addresses || [];
        const existingPaymentMethods = accountStore.account?.paymentMethods || [];
        
        console.log("[useAuth] Preserving existing payment methods:", {
          count: existingPaymentMethods.length,
          methods: existingPaymentMethods.map(pm => ({ id: pm.id, cardName: pm.cardName }))
        });
        
        accountStore.updateAccount({
          // Core identification fields
          id: userData.id,
          customerId: userData.customerId,
          username: userData.username,
          documentId: userData.documentId,

          // Authentication and status fields
          emailVerified: userData.emailVerified,
          identityVerified: userData.identityVerified,
          signedIn: true, // If we get here, user is signed in
          confirmed: userData.confirmed,
          blocked: userData.blocked,
          siteActiveId: userData.siteActiveId,

          // Email and contact info
          emailPrimary: userData.email || "",
          invoiceEmailAddress: userData.email || "",
          confirmEmail: userData.email || "",

          // Personal details
          salutation: userData.title || "",
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          dateOfBirth: userData.dateOfBirth || "",

          // Account actions & preferences
          password: "",
          confirmPassword: "",
          acceptTerms: true, // Assume true for existing users
          locale: userData.locale,

          // Timestamps (if available in API)
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt,
          publishedAt: userData.publishedAt,

          // Preserve existing persisted addresses and payment methods
          addresses: existingAddresses,
          paymentMethods: existingPaymentMethods,
        });

        console.log(
          "[useAuth] Account store initialized with basic user data from session"
        );
        
        // Load user account data (addresses, payment methods) after successful session restore
        loadUserAccountData().catch((error) => {
          console.warn("[getSession] Failed to load account data after session restore:", error);
        });
        
        // Return the user data to indicate successful authentication
        return userData;
      }

      // Special case: When refreshing during active development, don't clear account store
      // This helps prevent account data loss during refresh
      if (
        config.public.ENV_MODE === "development" &&
        window.location.pathname.includes("/checkout")
      ) {
        console.log(
          "[DEV MODE] Preserving account store during checkout page refresh"
        );
        // Don't clear user or account in dev mode during checkout
        return null;
      }

      // If no valid response and not in dev mode checkout, clear user state
      console.log("getSession - No valid response, clearing user state");
      authStore.unauthenticatedReason = "Session proxy returned no valid user data" + siteActiveIdDebug.value;
      authStore.setUser(null);

      // Also clear account store when user is logged out
      const accountStore = useAccountStore();
      if (accountStore.account) {
        // In production, always clear for security
        if (config.public.ENV_MODE !== "development") {
          accountStore.clearAccount();
          console.log(
            "[useAuth] Cleared account store due to no valid session"
          );
        } else {
          console.log(
            "[DEV MODE] Preserved account store despite invalid session"
          );
        }
      }

      return null;
    } catch (e: unknown) {
      console.error("Error fetching user:", e instanceof Error ? e.message : 'Unknown error');
      authStore.setUser(null);
      authStore.status = "unauthenticated";
      
      // Check if this is a session timeout (401 error)
      // Safely check error properties using type narrowing
      if (e && typeof e === 'object') {
        const err = e as any;
        if ((err.response?.status === 401) || (err.status === 401)) {
          handleSessionTimeout();
        }
      }
    } finally {
      authStore.loading = false;
    }
  };

  // Helper function to clear authentication cookies
  const clearAuthCookies = () => {
    if (typeof window !== 'undefined') {
      // Clear SITEACTIVEID and its signature cookie, plus session data cookies
      const cookiesToClear = [
        'SITEACTIVEID', 
        'SITEACTIVEID.sig',
        'auth_session_data',
        'auth_refresh'
      ];
      
      cookiesToClear.forEach(cookieName => {
        // Clear cookie with various path combinations to ensure complete removal
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        // Also try with leading dot for domain
        if (window.location.hostname.includes('.')) {
          const domain = '.' + window.location.hostname.split('.').slice(-2).join('.');
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
        }
      });
      
      // Also clear sessionStorage items related to auth session
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('auth_session_data');
        sessionStorage.removeItem('auth_session_last_refreshed');
      }
      
      console.log('[useAuth] Cleared authentication cookies:', cookiesToClear.join(', '));
      console.log('[useAuth] Cleared sessionStorage: auth_session_data, auth_session_last_refreshed');
    }
  };

  // Handle session timeout explicitly
  const handleSessionTimeout = () => {
    if (authStore.sessionTimeoutHandled) return;
    
    console.log("[useAuth] Explicitly handling session timeout");
    authStore.sessionTimeoutHandled = true;
    
    // Clear auth state
    authStore.setUser(null);
    authStore.status = "unauthenticated";
    authStore.loading = false;
    authStore.unauthenticatedReason = "Session expired";
    
    // Clear authentication cookies
    clearAuthCookies();

    // Manually clear the session cookie
    sessionCookie.value = null;

    // FIXED: Clear any verification flags in sessionStorage
    if (typeof window !== "undefined") {
      console.log("Clearing all verification flags on logout");
      sessionStorage.removeItem("verification_in_progress");
      sessionStorage.removeItem("verification_sent_time");
      // Also clear any 2FA bypass if present in dev mode
      sessionStorage.removeItem("dev_2fa_bypass");
      // Clear auth session cache items
      sessionStorage.removeItem("auth_session_data");
      sessionStorage.removeItem("auth_session_last_refreshed");
    }

    // Update status based on current state
    updateStatus();

    return true;
  };
  
  // Initialize session on startup
  const initSession = async () => {
    console.log("Initializing session...");
    try {
      await getSession(true);
      updateStatus();
      
      // Listen for explicit session timeout events
      if (typeof window !== "undefined") {
        window.addEventListener("force-auth-timeout", handleSessionTimeout);
        
        // Check URL for session_expired parameter
        const url = new URL(window.location.href);
        if (url.searchParams.get("session_expired") === "true") {
          handleSessionTimeout();
        }
      }
    } catch (e: unknown) {
      console.error("Error initializing session:", e instanceof Error ? e.message : 'Unknown error');
      authStore.status = "unauthenticated";
      authStore.loading = false;
    }
  };

  // Initialize session on module load - avoiding onMounted to prevent warnings
  // Only initialize in client-side context
  if (process.client) {
    console.log("Initializing session...");
    // Use nextTick to ensure this happens after component initialization if used in a component
    nextTick(() => {
      initSession();
    });
  }

  // Watch for cookie changes
  watch(sessionCookie, (newValue, oldValue) => {
    console.log("Cookie changed:", { newValue, oldValue });
    if (newValue !== oldValue) {
      // Only if actually changed
      if (!newValue) {
        authStore.setUser(null);
        updateStatus();
      } else {
        getSession();
      }
    }
  });

  // Watch for user changes
  watch(() => user.value, () => {
    updateStatus();
  });

  // Helper: Invalidate all API cache except products (for login/logout security)
  const invalidateAllCacheExceptProducts = () => {
    const cacheable = useCacheableBackendService();
    
    // CRITICAL SECURITY: Clear ALL user-specific cache to prevent cross-user data contamination
    if (typeof window !== "undefined") {
      try {
        const cacheData = JSON.parse(
          localStorage.getItem("go_api_cache") || "{}"
        );
        
        // Clear ALL cache entries except products
        const keysToKeep = Object.keys(cacheData).filter(key => 
          key.includes('products') || key.includes('product-')
        );
        
        // Create new cache with only product entries
        const cleanCache: Record<string, any> = {};
        keysToKeep.forEach(key => {
          cleanCache[key] = cacheData[key];
        });
        
        console.log("[Auth] SECURITY: Clearing all user-specific cache on logout");
        console.log(`[Auth] Kept ${keysToKeep.length} product cache entries, removed ${Object.keys(cacheData).length - keysToKeep.length} user-specific entries`);
        
        localStorage.setItem("go_api_cache", JSON.stringify(cleanCache));
        
      } catch (e: unknown) {
        console.error(
          "[Auth] SECURITY: Error clearing user cache - removing entire cache for safety:",
          e instanceof Error ? e.message : 'Unknown error'
        );
        // If there's any error, remove entire cache for security
        localStorage.removeItem("go_api_cache");
      }
    }
    
    // Also clear individual cacheable service caches
    cacheable.invalidateCache("user-profile");
    cacheable.invalidateCache("current-services");
    cacheable.invalidateCache("recharges");
  };

  // NEW: SITEACTIVEID-based cache validation for Flutter webview clients
  const validateAndClearStaleCache = (currentSiteActiveId?: string) => {
    if (typeof window === "undefined" || !currentSiteActiveId) return;
    
    try {
      const STORAGE_KEY = "go_last_siteactiveid";
      const lastSiteActiveId = localStorage.getItem(STORAGE_KEY);
      
      console.log("[Auth] Cache validation - Current:", currentSiteActiveId, "Last:", lastSiteActiveId);
      
      // If SITEACTIVEID has changed, clear stale cache
      if (lastSiteActiveId && lastSiteActiveId !== currentSiteActiveId) {
        console.warn("[Auth] 🚨 SITEACTIVEID MISMATCH DETECTED! Previous user data may be cached");
        console.log("[Auth] 🧹 Clearing ALL user-specific cache for new session");
        
        // Clear ALL user-specific cache immediately
        invalidateAllCacheExceptProducts();
        
        // Also clear any other sensitive data
        const sensitiveKeys = ['accountStore', 'userPreferences', 'recentActivity'];
        for (const key of sensitiveKeys) {
          localStorage.removeItem(key);
        }
        
        console.log("[Auth] ✅ Cache cleared for new user session");
      }
      
      // Update the stored SITEACTIVEID for next time
      localStorage.setItem(STORAGE_KEY, currentSiteActiveId);
      
    } catch (e: unknown) {
      console.error(
        "[Auth] Error in cache validation - clearing cache for safety:",
        e instanceof Error ? e.message : 'Unknown error'
      );
      // If there's any error, clear cache for safety
      invalidateAllCacheExceptProducts();
      localStorage.removeItem("go_api_cache");
    }
  };

  // Sign in
  const signIn = async (
    email: string,
    password: string,
    rememberMe: boolean = false
  ) => {
    console.log(`[useAuth] Signing in with rememberMe: ${rememberMe}`);
    try {
      authStore.loading = true;
      authStore.error = null;

      // Immediately clear all sensitive API cache except products on login
      // This prevents session bleed and stale user/service data
      invalidateAllCacheExceptProducts();

      // IMPORTANT: Force invalidate the GraphQL cache to prevent any stale data issues
      if (typeof window !== 'undefined') {
        try {
          invalidateAllGraphQLCache();
        } catch (e) {
          console.error('Failed to invalidate GraphQL cache:', e);
        }
      }

      // Call the backend service to sign in
      const response = await signInBackend(email, password);
      console.log("[DEBUG] Login response:", response);

      // Check for valid response
      if (!response) {
        console.error("Login failed - no response received");
        authStore.error = "Login failed. Please try again.";
        return false;
      }

      // Set cookie from response with appropriate expiration based on rememberMe
      if (response.siteActiveId) {
        const cookieOptions = rememberMe
          ? getPersistentCookieOptions()
          : getSessionCookieOptions();
        console.log(`[useAuth] Setting SITEACTIVEID cookie with options:`, {
          rememberMe,
          maxAge: cookieOptions.maxAge,
        });

        // Use direct useCookie with options to set the cookie with correct expiration
        const cookie = useCookie("SITEACTIVEID", cookieOptions);
        cookie.value = response.siteActiveId;
        sessionCookie.value = response.siteActiveId;
      }

      // Extract user data - it might be nested in response.user
      const userData = response.user || response;

      if (userData && userData.id) {
        console.log("Login successful, extracted user data");

        // Validate and clear stale cache immediately on successful login
        validateAndClearStaleCache(userData.siteActiveId);

        // Set user data from response
        authStore.setUser({
          id: userData.id,
          email: userData.email,
          username: userData.username,
          customerId: userData.customerId,
          title: userData.title,
          firstName: userData.firstName,
          lastName: userData.lastName,
          emailVerified: userData.emailVerified,
          identityVerified: userData.identityVerified,
          dateOfBirth: userData.dateOfBirth,
          siteActiveId: userData.siteActiveId || response.siteActiveId, // Fall back to top-level siteActiveId
        });

        console.log("Successfully extracted user data", user.value);

        // Initialize account store with user data when user signs in
        const accountStore = useAccountStore();
        if (user.value) {
          // Preserve any existing addresses and payment methods from localStorage persistence
          const existingAddresses = accountStore.account?.addresses || [];
          const existingPaymentMethods = accountStore.account?.paymentMethods || [];
          
          console.log("[useAuth] Preserving existing payment methods:", {
            count: existingPaymentMethods.length,
            methods: existingPaymentMethods.map(pm => ({ id: pm.id, cardName: pm.cardName }))
          });
          
          accountStore.updateAccount({
            // Core identification fields
            id: user.value.id,
            customerId: user.value.customerId,
            username: user.value.username,
            documentId: user.value.documentId,

            // Authentication and status fields
            emailVerified: user.value.emailVerified,
            identityVerified: user.value.identityVerified,
            confirmed: user.value.confirmed,
            blocked: user.value.blocked,
            siteActiveId: user.value.siteActiveId,
            signedIn: true,

            // Email and contact info
            emailPrimary: user.value.email || "",
            invoiceEmailAddress: user.value.email || "",
            confirmEmail: user.value.email || "",

            // Personal details
            salutation: user.value.title || "",
            firstName: user.value.firstName || "",
            lastName: user.value.lastName || "",
            dateOfBirth: user.value.dateOfBirth || "",

            // Account actions & preferences
            password: "",
            confirmPassword: "",
            acceptTerms: true,
            locale: user.value.locale,

            // Timestamps
            createdAt: user.value.createdAt,
            updatedAt: user.value.updatedAt,
            publishedAt: user.value.publishedAt,

            // Preserve existing persisted addresses and payment methods
            addresses: existingAddresses,
            paymentMethods: existingPaymentMethods,
          });

          console.log("[useAuth] Account store initialized with sign-in user data");
          
          // Load user account data (addresses, payment methods) after successful sign-in
          loadUserAccountData().catch((error) => {
            console.warn("[signIn] Failed to load account data after sign-in:", error);
          });
        }

        // PostHog: Track login event
        if (typeof window !== 'undefined') {
          try {
            const { trackLogin } = usePostHog();
            trackLogin('email');
          } catch (error) {
            console.warn('[PostHog] Failed to track login event:', error);
          }
        }

        return user.value;
      } else {
        // No valid user data found
        console.error("Login failed - response missing user data", response);
        authStore.error = "Login failed. Please try again.";
        return false;
      }
    } catch (err) {
      console.error("Failed to sign in:", err);
      authStore.error = "Invalid email or password";
      authStore.setUser(null);
      return false;
    } finally {
      authStore.loading = false;
      updateStatus();
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      authStore.loading = true;

      // PostHog: Track logout event before clearing data
      if (typeof window !== 'undefined') {
        try {
          const { trackLogout } = usePostHog();
          trackLogout();
        } catch (error) {
          console.warn('[PostHog] Failed to track logout event:', error);
        }
      }

      // Step 1: Call the signout API endpoint
      const response = await $fetch('/api/auth/signout', {
        method: 'POST',
        body: {
          customerId: user.value?.customerId
        }
      });
      
      console.log('[Auth] Official signout completed:', response);
      
    } catch (error) {
      console.error('[Auth] Signout API error:', error);
      // Continue with cleanup even if API fails
    } finally {
      // Step 2: Clear all state and caches
      await clearDataAndRedirect();
    }
  };

  // Helper function to clear all state and redirect after signout
  const clearDataAndRedirect = async () => {
    // Immediately clear all sensitive API cache except products on logout
    // This guarantees no sensitive data is left after logout, but preserves products for performance
    invalidateAllCacheExceptProducts();

    // Clear user state and update status
    authStore.setUser(null);

    // Also clear account store on sign out
    const accountStore = useAccountStore();
    if (accountStore.account) {
      accountStore.clearAccount();
      console.log("[useAuth] Cleared account store on sign out");
    }

    // Clear authentication cookies
    clearAuthCookies();

    // Manually clear the session cookie
    sessionCookie.value = null;

    // SECURITY: Clear all sensitive localStorage and sessionStorage items
    if (typeof window !== "undefined") {
      console.log("=== LOGOUT CLEANUP DEBUG ===");
      console.log("Clearing all verification flags and sensitive data on logout");
      
      // Clear sessionStorage items
      sessionStorage.removeItem("verification_in_progress");
      sessionStorage.removeItem("verification_sent_time");
      sessionStorage.removeItem("dev_2fa_bypass");
      sessionStorage.removeItem("auth_session_data");
      sessionStorage.removeItem("auth_session_last_refreshed");
      
      // Enhanced localStorage cleanup with verification
      const performLocalStorageCleanup = () => {
        // Collect ALL localStorage keys first to avoid iteration issues
        const allKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            allKeys.push(key);
          }
        }
        
        console.log("All localStorage keys found:", allKeys);
        
        // Define sensitive key patterns and explicit keys
        const sensitivePatterns = [
          { pattern: /^activation/, name: "activation" },
          { pattern: /^commerce/, name: "commerce" },
          { pattern: /^account/, name: "account" },
          { pattern: /^checkout/, name: "checkout" },
          { pattern: /^go_api_cache/, name: "go_api_cache" },
          { pattern: /Store$/, name: "store" }
        ];
        
        const explicitSensitiveKeys = [
          "activationSimNumber",
          "activationSelectedAddress",
          "accountStore",
          "activationStore",
          "commerceStore",
          "simpleOrderStore"
        ];
        
        // Filter out sensitive keys with detailed debugging
        const sensitiveKeys = allKeys.filter(key => {
          // Check explicit keys first
          if (explicitSensitiveKeys.includes(key)) {
            console.log(`Key "${key}" is explicitly sensitive`);
            return true;
          }
          
          // Check patterns
          for (const { pattern, name } of sensitivePatterns) {
            if (pattern.test(key)) {
              console.log(`Key "${key}" matches ${name} pattern`);
              return true;
            }
          }
          
          return false;
        });
        
        console.log("Sensitive keys to remove:", sensitiveKeys);
        
        // Remove all identified sensitive keys with immediate verification
        const actuallyRemoved = [];
        const failedToRemove = [];
        
        for (const key of sensitiveKeys) {
          console.log(`[Auth] Attempting to remove localStorage key: ${key}`);
          const valueBefore = localStorage.getItem(key);
          console.log(`[Auth] Value before removal for "${key}":`, valueBefore ? "EXISTS" : "NULL");
          
          localStorage.removeItem(key);
          
          // Immediate verification
          const valueAfter = localStorage.getItem(key);
          if (valueAfter === null) {
            console.log(`[Auth] Successfully removed: ${key}`);
            actuallyRemoved.push(key);
          } else {
            console.error(`[Auth] Failed to remove: ${key}`, valueAfter);
            failedToRemove.push(key);
          }
        }
        
        return { actuallyRemoved, failedToRemove, sensitiveKeys };
      };
      
      // Perform initial cleanup
      const initialCleanup = performLocalStorageCleanup();
      
      console.log(`[Auth] Initial cleanup: ${initialCleanup.actuallyRemoved.length}/${initialCleanup.sensitiveKeys.length} items removed`);
      if (initialCleanup.failedToRemove.length > 0) {
        console.error(`[Auth] Failed to remove items:`, initialCleanup.failedToRemove);
      }
      
      // Schedule a verification check after a delay to ensure items don't get re-added
      setTimeout(() => {
        console.log("=== VERIFYING CLEANUP AFTER 1 SECOND ===");
        const verification = performLocalStorageCleanup();
        
        if (verification.sensitiveKeys.length > 0) {
          console.error(`[Auth] Failed to remove items:`, verification.sensitiveKeys);
          console.error(`[Auth] This indicates something is re-adding localStorage items after logout`);
          
          // Try to remove them again
          verification.sensitiveKeys.forEach(key => {
            localStorage.removeItem(key);
            console.log(`[Auth] Re-removed: ${key}`);
          });
        } else {
          console.log(`[Auth] Verification successful: No sensitive data found after cleanup`);
        }
        
        console.log("=== CLEANUP VERIFICATION COMPLETE ===");
      }, 1000);
      
      console.log("=== LOGOUT CLEANUP COMPLETE ===");
    }
    
    // Update status based on current state
    updateStatus();

    return true;
  };

  // Load user account data (addresses, payments, etc.)
  const loadUserAccountData = async () => {
    try {
      // Only load if user is authenticated
      if (!user.value || status.value !== 'authenticated') {
        console.log("[loadUserAccountData] User not authenticated, skipping data load");
        return;
      }

      const accountStore = useAccountStore();
      if (!accountStore.account) {
        console.log("[loadUserAccountData] No account store initialized, skipping data load");
        return;
      }

      console.log("[loadUserAccountData] Loading user addresses and payment methods...");
      
      const { addressVerified, currentPayments } = useBackendService();
      
      // Fetch existing addresses
      const addressResult = await addressVerified();
      const userAddresses = addressResult?.body || [];
      
      // Fetch existing payment methods  
      const paymentResult = await currentPayments();
      const apiPaymentMethods = paymentResult?.body || [];
      
      console.log("[loadUserAccountData] Loaded data:", {
        addresses: userAddresses.length,
        paymentMethods: apiPaymentMethods.length
      });

      // Preserve locally added addresses that haven't been saved to backend yet
      const currentAddresses = accountStore.account.addresses || [];
      
      // Normalize local addresses by unwrapping _custom wrapper and ensuring consistent structure
      const normalizedLocalAddresses = currentAddresses
        .filter((addr: any) => 
          !(addr as any).id || // No backend ID (new address)
          (addr as any).id?.toString().startsWith('temp_') || // Temporary ID
          !userAddresses.find((apiAddr: any) => apiAddr.id === (addr as any).id) // Not found in API results
        )
        .map((addr: any) => {
          // If address has _custom wrapper, unwrap it and format to API structure (nested address)
          if (addr._custom && addr._custom.value) {
            const localAddr = addr._custom.value;
            return {
              id: localAddr.id || null,
              dateCreated: localAddr.dateCreated || new Date().toISOString(),
              dateUpdated: localAddr.dateUpdated || new Date().toISOString(),
              addressName: localAddr.addressName || "",
              address: {
                addressFull: `${localAddr.addressLine1 || localAddr.address?.addressLine1}${localAddr.addressLine2 || localAddr.address?.addressLine2 ? ' ' + (localAddr.addressLine2 || localAddr.address?.addressLine2) : ''}, ${localAddr.city || localAddr.suburb || localAddr.address?.suburb} ${localAddr.state || localAddr.address?.state?.abbreviation || localAddr.address?.state} ${localAddr.postcode || localAddr.postalCode || localAddr.address?.postalCode}`,
                addressLine1: localAddr.addressLine1 || localAddr.address?.addressLine1,
                addressLine2: localAddr.addressLine2 || localAddr.address?.addressLine2 || "",
                suburb: localAddr.city || localAddr.suburb || localAddr.address?.suburb,
                streetNumber: localAddr.streetNumber || localAddr.address?.streetNumber || "",
                streetNumberFirst: localAddr.streetNumber || localAddr.address?.streetNumber || "",
                state: typeof localAddr.state === 'object' 
                  ? localAddr.state 
                  : localAddr.address?.state || { abbreviation: localAddr.state },
                streetName: localAddr.streetName || localAddr.address?.streetName || "",
                streetLine: `${localAddr.streetNumber || localAddr.address?.streetNumber || ""} ${localAddr.streetName || localAddr.address?.streetName || ""}`.trim(),
                streetType: localAddr.streetType || localAddr.address?.streetType || "",
                streetTypeFull: localAddr.streetType || localAddr.address?.streetType || "",
                levelType: localAddr.flatUnitType || localAddr.address?.levelType || "",
                levelTypeFull: localAddr.flatUnitType || localAddr.address?.levelType || "",
                levelNumber: localAddr.flatUnitNumber || localAddr.address?.levelNumber || "",
                postalCode: localAddr.postcode || localAddr.postalCode || localAddr.address?.postalCode,
                longitude: 0,
                latitude: 0,
                streetLongitude: 0,
                streetLatitude: 0,
                suburbLongitude: 0,
                suburbLatitude: 0,
                meshblock: "",
                meshblock2016: "",
                meshblock2021: "",
                gnafId: "",
                valid: true,
              },
              defaultAddress: localAddr.defaultAddress || false,
            };
          }
          // If already in API format, return as-is
          return addr;
        });
      
      // Keep API addresses as-is (they're already in correct format)
      const normalizedApiAddresses = userAddresses;
      
      console.log("[loadUserAccountData] Normalized local addresses:", normalizedLocalAddresses.length);

      // Merge API addresses with normalized local addresses
      const mergedAddresses = [...normalizedApiAddresses, ...normalizedLocalAddresses];

      // Preserve any existing payment methods from localStorage persistence
      const currentPaymentMethods = accountStore.account?.paymentMethods || [];
      
      console.log("[loadUserAccountData] Preserving existing payment methods:", {
        count: currentPaymentMethods.length,
        methods: currentPaymentMethods.map(pm => ({ id: pm.id, cardName: pm.cardName }))
      });
      
      // Get API payment methods
      const userPaymentMethods = apiPaymentMethods;
      
      // Merge payment methods: API methods + locally stored NEW methods (id=0)
      const localNewPaymentMethods = currentPaymentMethods.filter(pm => pm.id === 0);
      const mergedPaymentMethods = [...userPaymentMethods, ...localNewPaymentMethods];
      
      console.log("[loadUserAccountData] Merged payment methods:", {
        api: userPaymentMethods.length,
        localNew: localNewPaymentMethods.length,
        total: mergedPaymentMethods.length
      });
      
      // Update account store with merged addresses and API payment methods
      accountStore.updateAccount({
        ...accountStore.account,
        addresses: mergedAddresses,
        paymentMethods: mergedPaymentMethods,
      });
      
      console.log("[loadUserAccountData] Account store updated with user data");
      
    } catch (error) {
      console.warn("[loadUserAccountData] Failed to load user account data:", error);
      // Don't throw - this is non-critical
    }
  };

  return {
    user,
    loading,
    error,
    status,
    signIn,
    signOut,
    getSession,
    loadUserAccountData,
    unauthenticatedReason,
    updateStatus,
    initSession,
    siteActiveIdDebug,
    handleSessionTimeout,
  };
};
